<template>
  <el-dialog
    :visible="visible"
    :before-close="handleClose"
    :title="dialogTitle || title"
    :width="width"
    :class="['common-dialog-table-dialog', dialogClass]"
    :append-to-body="appendToBody"
  >
    <div slot="title" class="materialDetailTitle">
      <div class="materialDetailTitle_title">{{ title }}</div>
    </div>
    <div class="taskDialog">
      <cardTable
        :showPaging="false"
        :tableColumn="tableColumn"
        :tableData="tableData"
        :mobileColumns="mobileColumnProps"
      />
    </div>
  </el-dialog>
</template>

<script>
import CardTable from './card-table.vue';

/**
 * 通用弹窗表格组件
 * 统一封装「标题 + 表格」的信息查看弹窗（如：产品问题记录、产品部件列表、任务单异常详情等），
 * 便于后续统一调整弹窗格式与样式。
 */
export default {
  name: 'CommonDialogTable',
  components: { CardTable },
  props: {
    // 是否显示弹窗
    visible: {
      type: Boolean,
      default: false
    },
    // 弹窗标题栏显示的完整标题（如："产品问题记录 - 产品ID: xxx"）
    title: {
      type: String,
      default: ''
    },
    // 传给 el-dialog 的 title 属性（未传时使用 title）
    dialogTitle: {
      type: String,
      default: ''
    },
    // 弹窗宽度
    width: {
      type: String,
      default: '80%'
    },
    // 自定义弹窗 class（用于扩展样式）
    dialogClass: {
      type: String,
      default: ''
    },
    // 是否渲染到 body
    appendToBody: {
      type: Boolean,
      default: false
    },
    // 表格列定义
    tableColumn: {
      type: Array,
      default: () => []
    },
    // 表格数据
    tableData: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    /** ✅ 手机端要显示的全部列（prop 列表）
     *  card-table 的逻辑：手机模式下如果没传 mobileColumns，
     *  就只显示列定义里 showOnMobile === true 的列 → 这里不传的话大部分列都会消失。
     *  所以把所有列的 prop 都传进去，保证手机 / 电脑看到的信息列完全一致。 */
    mobileColumnProps() {
      return (this.tableColumn || []).map(col => col.prop);
    }
  },
  methods: {
    /** 关闭弹窗（由父组件控制 visible） */
    handleClose() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped lang="scss">
/* ✅ 表格圆角：全站 CardInfoTable 统一用它，想改大小只动这一个变量 */
$table-radius: 6px;

/* ✅ 弹窗本体圆角：比表格略大一圈（外层 8px → 内层 6px），想改只动这一个变量 */
$dialog-radius: 8px;

.materialDetailTitle {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;

  .materialDetailTitle_title {
    font-size: 12px;
    font-weight: 500;
  }
}

/* ✅ 弹窗本体圆角（.el-dialog）
   ⚠️ class 写在 <el-dialog> 上会落到根元素 .el-dialog__wrapper，真正的弹窗盒子
      .el-dialog 是它的子元素 —— 所以必须用 ::v-deep 才能命中
      （同 StationInfoGas 里 .parts-dialog-dark ::v-deep .el-dialog 的写法） */
.common-dialog-table-dialog ::v-deep .el-dialog {
  border-radius: $dialog-radius;
  /* ✅ 把 header / body / footer 里任何铺到边角的背景色一起裁圆，
     否则内层有底色时四角会露出直角 */
  overflow: hidden;
}

.taskDialog {
  width: 100%;
  height: 60vh;
  overflow: hidden;
  /* ✅ 容器层圆角：overflow:hidden 把里面的表头 / 表体一起裁成同一个圆角 */
  border-radius: $table-radius;
}

/* ✅ 表格本体圆角：和容器同口径（$table-radius）
   表头只圆上面两角、表体只圆下面两角，拼起来才是完整的一圈；
   空数据占位块单独补下圆角，否则没数据时底部会是直角。 */
.taskDialog ::v-deep .el-table {
  border-radius: $table-radius;
  overflow: hidden;
}
.taskDialog ::v-deep .el-table__header-wrapper,
.taskDialog ::v-deep .el-table__fixed-header-wrapper {
  border-radius: $table-radius $table-radius 0 0;
  overflow: hidden;
}
.taskDialog ::v-deep .el-table__body-wrapper,
.taskDialog ::v-deep .el-table__fixed-body-wrapper,
.taskDialog ::v-deep .el-table__empty-block {
  border-radius: 0 0 $table-radius $table-radius;
}

/* 弹窗内容区：不再整体滚动，纵向滚动收敛到表格内部 */
::v-deep .el-dialog__body {
  overflow: hidden;
  padding: 5px 20px 20px; /* 顶部 padding 收窄为 10px，左右保持 20px */
}
</style>
